import React, { useState } from 'react';
import {
  HiOutlineChevronDown, HiOutlineChevronRight, HiOutlinePlay,
  HiOutlineFlag, HiOutlineCalendar, HiOutlineMicrophone,
  HiOutlineDocumentText, HiOutlineUserGroup, HiOutlineSparkles
} from 'react-icons/hi';

// Beginner-friendly help center for the Strategic Vision module.
// Pure content + an accordion; no data fetching. The "Start interactive tour"
// button hands control back to VisionMain to launch the spotlight tour.

const Section = ({ icon, title, subtitle, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="v26-guide-section">
      <button className="v26-guide-section-head" onClick={() => setOpen(o => !o)}>
        <span className="v26-guide-section-icon">{icon}</span>
        <span className="v26-guide-section-titles">
          <span className="v26-guide-section-title">{title}</span>
          {subtitle && <span className="v26-guide-section-sub">{subtitle}</span>}
        </span>
        {open ? <HiOutlineChevronDown size={18} /> : <HiOutlineChevronRight size={18} />}
      </button>
      {open && <div className="v26-guide-section-body">{children}</div>}
    </div>
  );
};

const Step = ({ n, children }) => (
  <div className="v26-guide-step">
    <span className="v26-guide-step-num">{n}</span>
    <span>{children}</span>
  </div>
);

// ── One field's explanation inside a field-by-field drawer ─────────────────
// tag: 'required' | 'optional' | 'auto'  → shows a small coloured pill.
const Field = ({ name, tag = 'optional', children, example }) => (
  <div className="v26-fg-field">
    <div className="v26-fg-field-name">
      {name}
      {tag === 'required' && <span className="v26-fg-req">Required</span>}
      {tag === 'optional' && <span className="v26-fg-opt">Optional</span>}
      {tag === 'auto' && <span className="v26-fg-auto">Automatic</span>}
    </div>
    <div className="v26-fg-desc">{children}</div>
    {example && <div className="v26-fg-eg"><b>Example:</b> {example}</div>}
  </div>
);

// ── Collapsible "Field-by-field" drawer; nests inside a Section ────────────
const FieldGuide = ({ title = 'Field-by-field', children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="v26-fg">
      <button className="v26-fg-head" onClick={() => setOpen(o => !o)}>
        <span>📝 {title}</span>
        {open ? <HiOutlineChevronDown size={15} /> : <HiOutlineChevronRight size={15} />}
      </button>
      {open && <div className="v26-fg-body">{children}</div>}
    </div>
  );
};

const VisionGuide = ({ onClose, onStartTour }) => {
  return (
    <div className="v26-drawer-overlay" onClick={onClose}>
      <div className="v26-drawer v26-guide-drawer" onClick={e => e.stopPropagation()}>
        <div className="v26-drawer-header" style={{ position: 'relative' }}>
          <h2>Strategic Vision — Guide</h2>
          <p>Everything you need to get started</p>
          <button className="v26-drawer-close" onClick={onClose}>×</button>
        </div>

        <div className="v26-drawer-body">
          {/* Start tour CTA */}
          <button className="v26-guide-tour-cta" onClick={onStartTour}>
            <HiOutlinePlay size={18} />
            <span>
              <strong>Take the interactive tour</strong>
              <span>A 60-second walkthrough that points out each part of the screen.</span>
            </span>
          </button>

          {/* ── How it all fits together ── */}
          <Section
            icon={<HiOutlineSparkles size={18} />}
            title="How it all fits together"
            subtitle="The big picture — read this first"
            defaultOpen
          >
            <p className="v26-guide-p">
              Strategic Vision turns a big yearly ambition into small, trackable
              pieces. Everything is organised as a tree — each level breaks the
              one above it into smaller work:
            </p>
            <div className="v26-guide-tree">
              <div className="v26-guide-tree-row" style={{ '--depth': 0 }}><b>Vision</b> — one per year (e.g. “Vision 2026”)</div>
              <div className="v26-guide-tree-row" style={{ '--depth': 1 }}><b>Mission</b> — a major outcome for the year</div>
              <div className="v26-guide-tree-row" style={{ '--depth': 2 }}><b>Goal</b> — a milestone inside a mission</div>
              <div className="v26-guide-tree-row" style={{ '--depth': 3 }}><b>Action Item</b> — a concrete deliverable</div>
              <div className="v26-guide-tree-row" style={{ '--depth': 4 }}><b>Task</b> — a unit of work someone does</div>
              <div className="v26-guide-tree-row" style={{ '--depth': 5 }}><b>Subtask</b> — a checklist tick</div>
            </div>
            <p className="v26-guide-p">
              <b>Progress flows upward automatically.</b> When you tick subtasks
              and complete tasks, the percentages on the goal, mission, and the
              whole vision recalculate on their own — weighted by importance. You
              never type a percentage by hand.
            </p>
            <p className="v26-guide-tip">
              💡 New here? The usual first step is to create a <b>Mission</b>, open
              its <b>Roadmap</b>, and add a <b>Goal</b> inside it.
            </p>
          </Section>

          {/* ── Mission Control ── */}
          <Section icon={<HiOutlineFlag size={18} />} title="Mission Control tab" subtitle="Create missions & build the roadmap">
            <p className="v26-guide-p">
              The home screen. Each card is a mission, showing its status, risk,
              team, completion bar, and a countdown to its target date.
            </p>

            <div className="v26-guide-howto-title">Create a mission</div>
            <Step n={1}>Pick the <b>Vision Year</b> at the top (e.g. 2026).</Step>
            <Step n={2}>Click <b>New Mission</b> in the banner.</Step>
            <Step n={3}>Fill in the form (every field is explained below).</Step>
            <Step n={4}>Click <b>Create Mission</b> — the new card appears on the board.</Step>

            <FieldGuide title="New Mission — every field">
              <Field name="Vision Year" tag="required" example="2026">
                The year this mission belongs to. The board groups everything by
                year; pick the year you want this mission to live under.
              </Field>
              <Field name="Target Date" tag="required" example="31 Dec 2026">
                The deadline for the whole mission. Drives the card countdown
                (“45d left”, or “12d overdue” in red) and feeds auto-risk detection.
              </Field>
              <Field name="Mission Name" tag="required" example="Launch New Website">
                A short, clear title. This is what everyone sees on the card and in
                dropdowns across the other tabs.
              </Field>
              <Field name="Description" tag="optional" example="Replace the old site with a faster, bilingual one.">
                What the mission aims to achieve. Helpful context for the team — it
                does not affect any calculation.
              </Field>
              <Field name="Mission Head" tag="optional" example="Sarah K.">
                The single person accountable for the mission (shown with a 👑). Pick
                from system users. Leave as “— None —” if undecided.
              </Field>
              <Field name="Status" tag="optional" example="Not Started">
                Not Started · In Progress · At Risk · On Hold · Completed. New
                missions usually start at <b>Not Started</b>. ⚠ Setting it to
                <b> Completed</b> marks every goal, action item, task & subtask
                underneath as Completed too.
              </Field>
              <Field name="Risk Level" tag="optional" example="Low">
                Your manual read of risk: Low / Medium / High. Separately, the system
                may show a <b>⚠ Auto</b> badge when progress is too slow to hit the
                target date — that one is calculated, not typed.
              </Field>
              <Field name="Assign Team" tag="optional" example="Ali, Maya, John">
                Search and add the people working on this mission. <b>Important:</b>
                non-admin users only see missions they are a team member (or head /
                coordinator) of — so add everyone who needs access.
              </Field>
              <Field name="Remarks" tag="optional" example="Budget approved in Q1.">
                Any initial note. Appears in italics at the bottom of the card.
              </Field>
              <Field name="Completion %" tag="auto">
                Not an input. Calculated from the weighted progress of the goals
                inside the mission. Build the roadmap and it fills itself in.
              </Field>
            </FieldGuide>

            <div className="v26-guide-howto-title">Step 3 — Build the roadmap (Goals → Action Items → Tasks → Subtasks)</div>
            <p className="v26-guide-p">
              Click a mission card (or <b>Open Roadmap →</b>) to open the roadmap
              drawer. Inside, you build the tree top-down. Each level carries a
              <b> weight %</b> (auto-shared between siblings) that decides how much it
              counts toward its parent.
            </p>
            <Step n={1}>Click <b>Add Goal</b> and fill the goal fields.</Step>
            <Step n={2}>Open the goal, click <b>Add Action Item</b>, fill its fields.</Step>
            <Step n={3}>Open the action item, click <b>Add task</b>, fill the task fields.</Step>
            <Step n={4}>Expand a task to add <b>subtasks</b> and attachments.</Step>

            <FieldGuide title="Goal — every field">
              <Field name="Goal name" tag="required" example="Redesign the homepage">
                The milestone you’re aiming for inside this mission.
              </Field>
              <Field name="Owner" tag="optional" example="Sara">
                The person responsible for the goal. Shown as an “Owner” pill.
              </Field>
              <Field name="Deadline" tag="optional" example="30 Jun 2026">
                When the goal should be done. Feeds the goal’s auto-risk flag.
              </Field>
              <Field name="Description" tag="optional" example="New layout, faster load, clearer CTAs.">
                Extra detail; shown in italics when the goal is expanded.
              </Field>
              <Field name="Weight %" tag="auto">
                How much this goal counts toward the mission. Shared automatically
                across the mission’s goals — you don’t set it by hand.
              </Field>
            </FieldGuide>

            <FieldGuide title="Action Item — every field">
              <Field name="Action item title" tag="required" example="Design mockups">
                A concrete deliverable that moves the goal forward.
              </Field>
              <Field name="Deadline" tag="optional" example="15 Jun 2026">
                Due date for this deliverable. Used for overdue & auto-risk flags.
              </Field>
              <Field name="Priority" tag="optional" example="High">
                Low · Medium · High · <b>Urgent</b>. Colour-coded pill; helps the
                team triage. Defaults to Medium.
              </Field>
              <Field name="Responsible" tag="optional" example="Sara, or “External: ACME agency”">
                One or more people accountable. You can pick <b>system users</b> or
                type a free-text <b>external name</b> (e.g. a vendor) — both are allowed.
              </Field>
              <Field name="Description" tag="optional" example="3 desktop + 3 mobile screens.">
                Optional detail shown when the action item is expanded.
              </Field>
              <Field name="Status / Weight %" tag="auto">
                Status (Pending → In Progress → Completed → Blocked) is set inline
                later; weight & completion % are calculated from its tasks.
              </Field>
            </FieldGuide>

            <FieldGuide title="Task — every field">
              <Field name="Task title" tag="required" example="Create wireframe">
                A single unit of work someone actually does.
              </Field>
              <Field name="Deadline" tag="optional" example="12 Jun 2026">
                When the task is due. Shows as “📅 due …” and powers the Daily Tracker
                date filter.
              </Field>
              <Field name="Assignees" tag="optional" example="Sara + external “Printer Co.”">
                Who does it — system users and/or external names. A task can have
                several assignees.
              </Field>
              <Field name="Status" tag="optional" example="In Progress">
                Pending · In Progress · Completed · Blocked. Change it inline on the
                card. Marking <b>Completed</b> ticks any remaining subtasks.
              </Field>
              <Field name="Completion % / Weight" tag="auto">
                Driven by how many subtasks are ticked (or the status if there are
                none). Rolls up into the action item, goal, mission and vision.
              </Field>
            </FieldGuide>

            <FieldGuide title="Subtask — every field">
              <Field name="Subtask title" tag="required" example="Sketch layout">
                A small checklist item under a task. Type it in the “+ subtask…” box
                and press Enter.
              </Field>
              <Field name="Done checkbox" tag="optional">
                Tick it when finished. This is the smallest action in the whole
                system — and ticking it is what moves every percentage above it.
              </Field>
              <Field name="Attachments" tag="optional" example="wireframe.pdf">
                Expand a task to attach files to the task itself (handy for proofs,
                drafts, references).
              </Field>
            </FieldGuide>

            <p className="v26-guide-tip">
              💡 Each level shows a <b>weight %</b> — that’s how much it counts
              toward its parent’s progress. Ticking the small items is what moves
              the big numbers.
            </p>
          </Section>

          {/* ── Daily Tracker ── */}
          <Section icon={<HiOutlineCalendar size={18} />} title="Daily Tracker tab" subtitle="Day-to-day task management">
            <p className="v26-guide-p">
              A flat, filterable list of tasks for getting work done day by day —
              without navigating the whole tree.
            </p>
            <ul className="v26-guide-list">
              <li>Filter by <b>mission</b>, <b>date</b>, or <b>assignee</b> to focus on what matters now.</li>
              <li>Change a task’s <b>status</b> (Pending → In Progress → Completed → Blocked) inline.</li>
              <li>Expand a task to tick subtasks or add a new one under any action item.</li>
            </ul>

            <FieldGuide title="Add Task (here) — every field">
              Click + Task beside an Action Item to create a new task.
The Mission, Goal, and Action Item are automatically inherited from the location where the task is created.
Only the task-specific details need to be entered.
              
              <Field name="Task Title" tag="required" example="Create wireframe">
                What needs doing.
              </Field>
              <Field name="Deadline" tag="optional" example="12 Jun 2026">
                Due date (optional).
              </Field>
              <Field name="Assignees" tag="optional" example="Sara, external “Printer Co.”">
                System users and/or external names.
              </Field>
            </FieldGuide>
          </Section>

          {/* ── Meeting Minutes ── */}
          <Section icon={<HiOutlineMicrophone size={18} />} title="Meeting Minutes tab" subtitle="Record, transcribe & capture decisions">
            <p className="v26-guide-p">
              Capture a meeting and turn it into structured follow-ups. You can
              type minutes manually, or let AI do the heavy lifting.
            </p>
            <div className="v26-guide-howto-title">Record a meeting with AI</div>
            <Step n={1}>Click <b>Add Minutes</b>, then pick the mission, goal, and date.</Step>
            <Step n={2}>Choose one or more <b>agendas</b> (or add a new one) so the AI knows what to listen for.</Step>
            <Step n={3}>Press <b>record</b> (or upload an existing audio file). It autosaves to a server <b>draft</b> as you go.</Step>
            <Step n={4}>Stop, then click <b>Transcribe &amp; Auto-Fill</b>. AI fills in discussion points, action items, and tasks for you.</Step>
            <Step n={5}>Review and edit, then <b>Save Meeting</b>. The action items flow straight into the roadmap.</Step>

            <FieldGuide title="Add Minutes — every field">
              <Field name="Mission" tag="required" example="Launch New Website">
                Which mission the meeting is about; filters the Goal list.
              </Field>
              <Field name="Goal" tag="required" example="Redesign the homepage">
                The meeting is filed under a goal. Agendas and saved action items
                attach here.
              </Field>
              <Field name="Meeting Date" tag="required" example="Today">
                When the meeting happened. Defaults to today.
              </Field>
              <Field name="Next Meeting" tag="optional" example="next Monday">
                Optional date of the follow-up meeting.
              </Field>
              <Field name="Meeting Title" tag="optional" example="Weekly leadership sync">
                A short title. If left blank, AI auto-fills it from the recording.
              </Field>
              <Field name="Participants" tag="optional" example="Sara, Ali, external “Client”">
                Who attended — system users and/or external names.
              </Field>
              <Field name="Agendas" tag="optional" example="Website progress, Budget">
                Per-goal topics shown as chips. Select any that apply, or type a new
                one and click Add. AI buckets the discussion under whichever agendas
                you select.
              </Field>
              <Field name="Audio (record / upload)" tag="optional">
                Record live or upload a file (mp3, wav, m4a, webm…). It streams to a
                server draft so nothing is lost if the tab closes — resume from
                <b> My Drafts</b> later.
              </Field>
              <Field name="Discussion Points → Action Items → Tasks" tag="optional">
                The body of the minutes. AI drafts these from the audio, but nothing
                is locked — edit any field, or add points / action items / tasks by
                hand before saving. Each action item here can carry a deadline,
                priority and responsible person.
              </Field>
            </FieldGuide>

            <p className="v26-guide-tip">
              💡 <b>Drafts</b> are saved on the server for 30 days — if you close
              the tab mid-meeting, resume from the <b>My Drafts</b> panel later.
            </p>
          </Section>

          {/* ── Weekly Summary ── */}
          <Section icon={<HiOutlineDocumentText size={18} />} title="Weekly Summary tab" subtitle="Week-by-week progress reports">
            <p className="v26-guide-p">
              A roll-up of what moved each week, with an optional AI-generated
              summary. Useful for status updates and reviews — pick a week and see
              what was completed, what’s in progress, and what slipped.
            </p>

            <FieldGuide title="New Summary — every field">
              <Field name="Mission" tag="required" example="Launch New Website">
                Which mission this summary covers.
              </Field>
              <Field name="Week Start" tag="required" example="Mon 1 Jun 2026">
                Pick any day in the week — it snaps to Monday and auto-fills the
                Week End and Week Number for you.
              </Field>
              <Field name="Week End / Week Number" tag="auto">
                Calculated from Week Start; read-only.
              </Field>
              <Field name="Generate with AI" tag="optional">
                Optional. Choose a data range (max 10 days) and AI drafts the four
                text fields and the mood from that period’s activity.
              </Field>
              <Field name="Team Mood" tag="optional" example="4 — Good">
                A 1–5 score (Very Low → Excellent) for how the week felt.
              </Field>
              <Field name="Key Achievements" tag="optional" example="Homepage mockups signed off.">
                What the team accomplished.
              </Field>
              <Field name="Issues / Blockers" tag="optional" example="Waiting on client logo files.">
                What’s blocking progress.
              </Field>
              <Field name="Support Required" tag="optional" example="Need a second developer.">
                What help the team needs.
              </Field>
              <Field name="Next Week Plan" tag="optional" example="Start the checkout page.">
                What’s planned for the coming week.
              </Field>
            </FieldGuide>
          </Section>

          {/* ── Accountability ── */}
          <Section icon={<HiOutlineUserGroup size={18} />} title="Accountability tab" subtitle="Who owns what">
            <p className="v26-guide-p">
              A people-centric view. It gathers every mission head, team member,
              and action-item owner into one place so you can see each person’s
              responsibilities and spot overdue items at a glance.
            </p>
          </Section>

          {/* ── Statuses & risk ── */}
          <Section icon={<HiOutlineSparkles size={18} />} title="Statuses, risk & colours" subtitle="What the badges mean">
            <ul className="v26-guide-list">
              <li><b>Not Started</b> — created but no work yet.</li>
              <li><b>In Progress</b> — actively being worked on.</li>
              <li><b>At Risk</b> — unlikely to hit the deadline.</li>
              <li><b>On Hold</b> — paused for now.</li>
              <li><b>Blocked</b> — stuck waiting on something.</li>
              <li><b>Completed</b> — done. Completing a parent ticks everything under it.</li>
            </ul>
            <p className="v26-guide-p">
              <b>Risk level</b> (Low/Medium/High) is set manually, but a <b>⚠ Auto</b>
              badge can also appear when the system detects an item is moving too
              slowly to finish on time.
            </p>
          </Section>

          {/* ── Permissions ── */}
          <Section icon={<HiOutlineUserGroup size={18} />} title="What can I do here?" subtitle="Permissions & roles">
            <ul className="v26-guide-list">
              <li>You’ll only see missions you’re a <b>team member</b> or coordinator of (admins see all).</li>
              <li><b>Create / edit</b> buttons appear only if you have those permissions on the Vision module.</li>
              <li><b>Coordinators</b> can view every mission in a vision year.</li>
              <li>Can’t see something you expect? Ask a coordinator or admin to add you to the mission.</li>
            </ul>
          </Section>

          <div className="v26-guide-footer">
            You can reopen this guide anytime from the <b>?</b> button in the header.
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisionGuide;
